import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { generateCommunicationInsights } from '../../../services/communicationAIService';

const CommunicationReportModal = ({ isOpen, onClose }) => {
  const [timeRange, setTimeRange] = useState('last_7_days');
  const [insights, setInsights] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const timeRangeOptions = [
    { value: 'last_7_days', label: 'آخر 7 أيام' },
    { value: 'last_30_days', label: 'آخر 30 يوم' },
    { value: 'last_3_months', label: 'آخر 3 أشهر' },
    { value: 'last_6_months', label: 'آخر 6 أشهر' },
    { value: 'last_year', label: 'آخر سنة' },
  ];

  // Mock communication data
  const mockCommunicationData = {
    totalMessages: 450,
    whatsappMessages: 280,
    smsMessages: 100,
    emailMessages: 70,
    responseRate: 78,
    averageResponseTime: '2.5 hours',
    topPerformingChannel: 'WhatsApp',
    clientEngagement: 'High',
    followUpCompletion: 85,
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    try {
      const generatedInsights = await generateCommunicationInsights({
        communicationData: mockCommunicationData,
        timeRange,
        metrics: 'response_rate, engagement, channel_performance',
      });
      setInsights(generatedInsights);
    } catch (error) {
      alert(error?.message || 'فشل في توليد التقرير');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportReport = () => {
    alert('جاري تصدير التقرير...');
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="تقرير التواصل" 
      size="xl"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-6" dir="rtl">
        {/* Time Range Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            الفترة الزمنية
          </label>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            options={timeRangeOptions}
          />
        </div>

        {/* Statistics Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="MessageSquare" size={18} color="#3B82F6" />
              <span className="text-xs text-gray-400">إجمالي الرسائل</span>
            </div>
            <div className="text-2xl font-bold text-white">{mockCommunicationData?.totalMessages}</div>
          </div>

          <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CheckCircle" size={18} color="#10B981" />
              <span className="text-xs text-gray-400">معدل الرد</span>
            </div>
            <div className="text-2xl font-bold text-white">{mockCommunicationData?.responseRate}%</div>
          </div>

          <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={18} color="#8B5CF6" />
              <span className="text-xs text-gray-400">متوسط الرد</span>
            </div>
            <div className="text-lg font-bold text-white">{mockCommunicationData?.averageResponseTime}</div>
          </div>

          <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="TrendingUp" size={18} color="#F59E0B" />
              <span className="text-xs text-gray-400">المتابعة</span>
            </div>
            <div className="text-2xl font-bold text-white">{mockCommunicationData?.followUpCompletion}%</div>
          </div>
        </div>

        {/* Channel Breakdown */}
        <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
          <h4 className="text-md font-semibold text-white mb-4">توزيع قنوات الاتصال</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="MessageCircle" size={16} color="#25D366" />
                <span className="text-sm text-gray-300">واتساب</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${(mockCommunicationData?.whatsappMessages / mockCommunicationData?.totalMessages) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-white w-12 text-left">
                  {mockCommunicationData?.whatsappMessages}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="MessageSquare" size={16} color="#3B82F6" />
                <span className="text-sm text-gray-300">SMS</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500"
                    style={{ width: `${(mockCommunicationData?.smsMessages / mockCommunicationData?.totalMessages) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-white w-12 text-left">
                  {mockCommunicationData?.smsMessages}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={16} color="#F59E0B" />
                <span className="text-sm text-gray-300">بريد إلكتروني</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-500"
                    style={{ width: `${(mockCommunicationData?.emailMessages / mockCommunicationData?.totalMessages) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-white w-12 text-left">
                  {mockCommunicationData?.emailMessages}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Generate AI Insights Button */}
        <Button
          onClick={handleGenerateReport}
          disabled={isGenerating}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Icon name="Loader" size={18} className="animate-spin" />
              <span>جاري التحليل...</span>
            </>
          ) : (
            <>
              <Icon name="Sparkles" size={18} />
              <span>تحليل بالذكاء الاصطناعي</span>
            </>
          )}
        </Button>

        {/* AI Insights Display */}
        {insights && (
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-lg p-5 space-y-5">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Brain" size={22} color="#A78BFA" />
              <span className="text-xl font-bold text-purple-400">رؤى ذكية</span>
            </div>

            {/* Key Trends */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="TrendingUp" size={18} color="#10B981" />
                <span className="text-md font-semibold text-green-400">الاتجاهات الرئيسية</span>
              </div>
              <ul className="space-y-2">
                {insights?.keyTrends?.map((trend, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                    <Icon name="ChevronLeft" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                    <span>{trend}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Response Rate Analysis */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="BarChart3" size={18} color="#3B82F6" />
                <span className="text-md font-semibold text-blue-400">تحليل معدل الرد</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{insights?.responseRateAnalysis}</p>
            </div>

            {/* Best Channels */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Award" size={18} color="#F59E0B" />
                <span className="text-md font-semibold text-yellow-400">أفضل القنوات</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {insights?.bestChannels?.map((channel, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm"
                  >
                    {channel}
                  </span>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Lightbulb" size={18} color="#8B5CF6" />
                <span className="text-md font-semibold text-purple-400">التوصيات</span>
              </div>
              <ul className="space-y-2">
                {insights?.recommendations?.map((recommendation, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                    <Icon name="CheckCircle" size={16} color="#8B5CF6" className="mt-0.5 flex-shrink-0" />
                    <span>{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Engagement Insights */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Users" size={18} color="#EC4899" />
                <span className="text-md font-semibold text-pink-400">رؤى التفاعل</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{insights?.engagementInsights}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-700">
          {insights && (
            <Button onClick={handleExportReport} variant="secondary" className="flex-1">
              <Icon name="Download" size={18} />
              <span>تصدير التقرير</span>
            </Button>
          )}
          <Button onClick={onClose} variant="secondary" className="flex-1">
            إغلاق
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CommunicationReportModal;